import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useLocation, useNavigate, useParams } from "react-router";
import _ from "lodash";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import {
  VStack,
  HStack,
  ButtonPrimary1,
  ButtonPrimary,
  Error,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { SwitchButton } from "../../../components/shared/SwitchButton";
import {
  FmvPriceType,
  TransactionDetailsData,
} from "../../../types/Transaction";
import {
  MonetaryEventState,
  RecurringIntervalUnit,
} from "../../../types/OptionBuyback";
import { useValuationDetails } from "../../../queries/Valuation";
import { getCurrencySymbol } from "../../../utils/currencyFormatter";
import { useCompanyStore } from "../../../store";
import Tooltip from "../../../components/shared/Tooltip";
import {
  deleteExerciseTransaction,
  deleteTransactionDetails,
  saveTransactionDetails,
} from "../../../api/TransactionApisV2";
import { useDeleteTransaction } from "../../../queries/transactionV2";
import AlertDialog from "../../../components/shared/AlertDialog";

interface BasicExerciseDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
}

const BasicExerciseDetails = (props: BasicExerciseDetailsProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    validateForm,
    setTouched,
  } = useFormikContext<TransactionDetailsData>();
  const currencySymbol = getCurrencySymbol();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const valuations = useValuationDetails();
  const { mutate: deleteTransaction } = useDeleteTransaction();
  const { id, mode: paramsMode } = params;
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || paramsMode;

  function touchAllFields(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(touchAllFields);
    }
    if (obj !== null && typeof obj === "object") {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = touchAllFields(obj[key]);
        return acc;
      }, {} as any);
    }
    return true;
  }

  const handleSubmit = async () => {
    const validationErrors = await validateForm();
    if (Object.keys(validationErrors).length === 0) {
      if (!id) {
        await saveTransactionDetails(values);
      }
      props.onStepChange();
    } else {
      setTouched(touchAllFields(values), true);
    }
  };

  const [inputDisabled, setInputDisabled] = useState(false);

  useEffect(() => {
    if (!values.eventState || values.eventState === MonetaryEventState.DRAFT) {
      setInputDisabled(false);
    } else {
      setInputDisabled(true);
    }
  }, [values.eventState]);

  const calculateEndDate = (e: any, type: "IntervalUnit" | "Interval") => {
    if (values.recurringSetting.isRecurring) {
      if (
        type === "Interval" &&
        (e.target.value === "" || parseInt(e.target.value, 10) <= 0)
      ) {
        return 0;
      }
      const startDate = new Date(values.startDate);
      const interval =
        type === "Interval"
          ? parseInt(e.target.value, 10)
          : values.recurringSetting.recurringInterval;
      const intervalUnit =
        type === "Interval"
          ? values.recurringSetting.recurringIntervalUnit
          : e.target.value;

      const endDate = new Date(startDate);

      switch (intervalUnit) {
        case RecurringIntervalUnit.DAYS:
          endDate.setDate(endDate.getDate() + interval);
          break;
        case RecurringIntervalUnit.MONTH:
          endDate.setMonth(endDate.getMonth() + interval);
          break;
        case RecurringIntervalUnit.QUARTER:
          endDate.setMonth(endDate.getMonth() + interval * 3);
          break;
        case RecurringIntervalUnit.YEAR:
          endDate.setFullYear(endDate.getFullYear() + interval);
          break;
        default:
          break;
      }
      setFieldValue("endDate", endDate.toISOString().split("T")[0]);
    }
    return "";
  };
  const { companyData } = useCompanyStore();

  const handleDiscard = async () => {
    await deleteTransactionDetails();
    navigate("/options/esopTransactions");
  };

  const [deleteDialogParams, setDeleteDialogParams] = useState<{
    open: boolean;
  }>({ open: false });

  return (
    <VStack className="w-full">
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501 ">
          Exercise - Basic Details
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Set up key details like name, exercise window, and other basic
          parameters to get started. You’ll get guided help along the way to
          configure everything smoothly.
        </HStack>
      </VStack>
      <VStack className="gap-4 pt-4">
        <HStack className="gap-8 px-6">
          <VStack className="flex-1">
            <Label className="text-sm font-normal">Exercise Name</Label>
            <Input
              type="text"
              name="eventName"
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.eventName}
              disabled={inputDisabled}
              placeholder="Enter Exercise Name"
            />
            {touched.eventName && errors.eventName && (
              <Error text={errors.eventName}></Error>
            )}
          </VStack>
          <VStack className="flex-1">
            <Label className="text-sm font-normal">Participant List</Label>
            <Select
              placeholder="--Select--"
              name="participantListMethod"
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              disabled={mode === "edit" || inputDisabled}
              options={["Upload Excel", "Generate By Filters"]}
              value={values.monetaryDetails.participantListMethod}
              onChange={(e) => {
                if (inputDisabled) return;
                if (e.target.value === "Upload Excel") {
                  setFieldValue("monetaryDetails.isLatestFMV", false);
                  setFieldValue("recurringSetting.isRecurring", false);
                }
                setFieldValue(
                  "monetaryDetails.participantListMethod",
                  e.target.value
                );
              }}
              onBlur={handleBlur}
            />
            {touched.monetaryDetails?.participantListMethod &&
              errors.monetaryDetails?.participantListMethod && (
                <Error
                  text={errors.monetaryDetails?.participantListMethod}
                ></Error>
              )}
          </VStack>
        </HStack>
        <Dialog
          open={deleteDialogParams.open}
          onClose={() => setDeleteDialogParams({ open: false })}
          maxWidth="md"
        >
          {deleteDialogParams.open && (
            <AlertDialog
              message={`Are you sure you want to discard`}
              primaryActionText="Delete"
              onClose={() => setDeleteDialogParams({ open: false })}
              secondaryActionText="Cancel"
              onPrimaryAction={() => {
                handleDiscard();
                setDeleteDialogParams({ open: false });
              }}
              onSecondaryAction={() => {
                setDeleteDialogParams({ open: false });
              }}
              open={deleteDialogParams.open}
            />
          )}
        </Dialog>
        <HStack className="gap-8 px-6 py-2">
          {
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="text-sm font-normal">Fair Market Value</Label>
                <div className="justify-end">
                  <SwitchButton
                    onClick={() => {
                      if (inputDisabled) return;
                      if (
                        !values.monetaryDetails.isLatestFMV &&
                        companyData?.companyMetaData?.typeOfTheCompany ===
                          "PRIVATE" &&
                        (!valuations.data ||
                          (valuations.data && valuations.data?.length === 0))
                      ) {
                        toast.error(
                          "Please upload Valuation Report to use this"
                        );
                        return;
                      }
                      setFieldValue(
                        "monetaryDetails.isLatestFMV",
                        !values.monetaryDetails.isLatestFMV
                      );
                    }}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onBlur={handleBlur}
                    value={values.monetaryDetails.isLatestFMV}
                    label=""
                    disabled={inputDisabled}
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                The FMV chosen as default is from the last valuation report
                present on the platform, please change the FMV as required
              </p>
            </div>
          }
          {!values.monetaryDetails.isLatestFMV ? (
            <VStack className="flex-1">
              <Label className="text-sm font-normal">
                FMV Price ({currencySymbol})
              </Label>
              <VStack>
                <Input
                  type="number"
                  name="monetaryDetails.fmvPrice"
                  placeholder="Enter FMV Price"
                  disabled={inputDisabled}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    if (inputDisabled) return;
                    const numericValue = e.target.value
                      ? parseFloat(e.target.value)
                      : "";
                    setFieldValue("monetaryDetails.fmvPrice", numericValue);
                  }}
                  value={
                    values.monetaryDetails.fmvPrice === 0
                      ? ""
                      : values.monetaryDetails.fmvPrice
                  }
                />
                {touched.monetaryDetails?.fmvPrice &&
                  errors.monetaryDetails?.fmvPrice && (
                    <Error text={errors.monetaryDetails?.fmvPrice}></Error>
                  )}
              </VStack>
            </VStack>
          ) : (
            <div className="flex-1"></div>
          )}
        </HStack>
        {values.monetaryDetails.isLatestFMV &&
          values.monetaryDetails &&
          companyData?.companyMetaData?.typeOfTheCompany === "PUBLIC" && (
            <HStack className="gap-8 px-6 py-2">
              <VStack className="flex-1">
                <Label className="text-sm font-normal">FMV Price Type</Label>
                <Select
                  name="monetaryDetails.fmvPriceType"
                  placeholder="--Select--"
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  onChange={handleChange}
                  options={[
                    FmvPriceType.PREVIOUS_DAY_CLOSING,
                    FmvPriceType.EXERCISE_DAY_AVERAGE,
                  ]}
                  disabled={inputDisabled}
                  textGetter={(option) => option.replaceAll("_", " ")}
                  valueGetter={(option) => option}
                  onBlur={handleBlur}
                  value={values.monetaryDetails.fmvPriceType}
                ></Select>
                {touched.monetaryDetails?.fmvPriceType &&
                  errors.monetaryDetails?.fmvPriceType && (
                    <Error text={errors.monetaryDetails?.fmvPriceType}></Error>
                  )}
              </VStack>
              <VStack className={"flex-1"}></VStack>
            </HStack>
          )}
        {values.monetaryDetails.participantListMethod !== "Upload Excel" && (
          <HStack className="gap-8 px-6 py-2">
            <VStack className="flex-1">
              <Label className="text-sm font-normal">
                Percentage of Net Vested Options
              </Label>
              <Input
                type="number"
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                placeholder="Enter Percentage"
                disabled={inputDisabled}
                name="monetaryDetails.percentageOfNetVestedOptions"
                max={100}
                min={0}
                onChange={handleChange}
                onBlur={handleBlur}
                value={
                  values.monetaryDetails.percentageOfNetVestedOptions === 0
                    ? ""
                    : values.monetaryDetails.percentageOfNetVestedOptions
                }
              />
            </VStack>
            <VStack className="flex-1"></VStack>
          </HStack>
        )}
        <div className="pt-6 pb-2 text-base font-medium border-b ">
          <div className="flex justify-between pl-6">Time Period</div>
        </div>
        <HStack className="gap-8 px-6">
          <div className="flex-1">
            <Label className="text-sm font-normal">Start Date</Label>
            <Input
              type="date"
              disabled={inputDisabled}
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              name="startDate"
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                values.startDate
                  ? new Date(values.startDate).toISOString().split("T")[0]
                  : ""
              }
            />
            {touched.startDate && errors.startDate && (
              <Error text={errors.startDate}></Error>
            )}
          </div>
          <div className="flex-1">
            <p
              className={` form-label text-sm font-medium w-2/3 inline-flex mb-0 gap-1`}
            >
              Vesting CutOff Date
              <Tooltip
                text={
                  "Only Options vested up to and including this date are eligible for exercise."
                }
                _className="w-96 left-4"
              >
                <Icon
                  icon="material-symbols:info-outline-rounded"
                  height={20}
                  width={20}
                  className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark"
                />
              </Tooltip>
            </p>
            <Input
              type="date"
              disabled={inputDisabled}
              className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
              name="vestingCutOffDate"
              onChange={handleChange}
              onBlur={handleBlur}
              value={
                values.vestingCutOffDate
                  ? new Date(values.vestingCutOffDate)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
            />
            {touched.vestingCutOffDate && errors.vestingCutOffDate && (
              <Error text={errors.vestingCutOffDate}></Error>
            )}
          </div>
        </HStack>
        <HStack className="gap-8 px-6 py-2">
          {values.monetaryDetails.participantListMethod !== "Upload Excel" ? (
            <div className="flex-1 pt-4">
              <HStack className="justify-between gap-2 ">
                <Label className="text-sm font-normal">
                  Recurring Exercise
                </Label>
                <div className="justify-end">
                  <SwitchButton
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onClick={() => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "recurringSetting.isRecurring",
                        !values.recurringSetting.isRecurring
                      );
                    }}
                    onBlur={handleBlur}
                    value={values.recurringSetting.isRecurring}
                    label=""
                  />
                </div>
              </HStack>
              <p className="font-medium text-gray-400 text-xxs">
                Enabling this would keep repeating the exercise indefinately
                until it is paused or closed manually
              </p>
            </div>
          ) : (
            <div className="flex-1 pt-4"></div>
          )}
          {values.recurringSetting.isRecurring ? (
            <VStack className="flex-1">
              <HStack>
                <Label className="text-sm font-normal">Repeat Every</Label>
              </HStack>
              <HStack className="w-full gap-2">
                <VStack>
                  <Input
                    type="number"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    name="monetaryDetails.fmvPrice"
                    placeholder="Interval"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      if (inputDisabled) return;
                      const numericValue = e.target.value
                        ? parseInt(e.target.value, 10)
                        : "";
                      setFieldValue(
                        "recurringSetting.recurringInterval",
                        numericValue
                      );
                      calculateEndDate(e, "Interval");
                    }}
                    value={
                      values.recurringSetting.recurringInterval === 0
                        ? ""
                        : values.recurringSetting.recurringInterval
                    }
                  />
                  {touched.recurringSetting?.recurringInterval &&
                    errors.recurringSetting?.recurringInterval && (
                      <Error
                        text={errors.recurringSetting?.recurringInterval}
                      ></Error>
                    )}
                </VStack>
                <VStack>
                  <Select
                    placeholder="--Select--"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    options={[
                      RecurringIntervalUnit.DAYS,
                      RecurringIntervalUnit.MONTH,
                      RecurringIntervalUnit.QUARTER,
                      RecurringIntervalUnit.YEAR,
                    ]}
                    onBlur={handleBlur}
                    value={values.recurringSetting.recurringIntervalUnit}
                    onChange={(e) => {
                      if (inputDisabled) return;
                      setFieldValue(
                        "recurringSetting.recurringIntervalUnit",
                        e.target.value
                      );
                      calculateEndDate(e, "IntervalUnit");
                    }}
                  />
                  {touched.recurringSetting?.recurringIntervalUnit &&
                    errors.recurringSetting?.recurringIntervalUnit && (
                      <Error
                        text={errors.recurringSetting?.recurringIntervalUnit}
                      ></Error>
                    )}
                </VStack>
              </HStack>
            </VStack>
          ) : (
            <div className="flex-1">
              <Label className="text-sm font-normal">End Date</Label>
              <Input
                type="date"
                disabled={inputDisabled}
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                name="endDate"
                onChange={handleChange}
                onBlur={handleBlur}
                value={
                  values.endDate
                    ? new Date(values.endDate).toISOString().split("T")[0]
                    : ""
                }
              />
              {touched.endDate && errors.endDate && (
                <Error text={errors.endDate}></Error>
              )}
            </div>
          )}
        </HStack>
        {values.recurringSetting.isRecurring && (
          <div className="flex-1 px-6">
            <VStack className="flex-1">
              <HStack className="justify-between gap-2 ">
                <VStack>
                  <Label className="text-sm font-normal">
                    Recurrance Occurances
                  </Label>
                  <p className="font-medium text-gray-400 text-xxs">
                    These are the number of times the exercise will take place
                    before it automatically closes
                  </p>
                </VStack>
                <VStack className="justify-end">
                  <Input
                    type="number"
                    name="recurringSetting.noOfRecurrences"
                    disabled={inputDisabled}
                    className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                    onBlur={handleBlur}
                    placeholder="Number of Occurances"
                    onChange={(e) => {
                      if (inputDisabled) return;
                      const numericValue = e.target.value
                        ? parseInt(e.target.value, 10)
                        : "";
                      setFieldValue(
                        "recurringSetting.noOfRecurrences",
                        numericValue
                      );
                    }}
                    value={
                      values.recurringSetting.noOfRecurrences === 0
                        ? ""
                        : values.recurringSetting.noOfRecurrences
                    }
                  />
                  {touched.recurringSetting?.noOfRecurrences &&
                    errors.recurringSetting?.noOfRecurrences && (
                      <Error
                        text={errors.recurringSetting?.noOfRecurrences}
                      ></Error>
                    )}
                </VStack>
              </HStack>
            </VStack>
          </div>
        )}
        <div className="pt-6 pb-2 text-base font-medium border-b ">
          <div className="flex justify-between pl-6">Transaction Settings</div>
        </div>
        <HStack className="gap-8 px-6">
          <div className="flex-1 pt-4">
            <HStack className="justify-between gap-2 ">
              <Label className="text-sm font-normal">
                Ask Willingness from Employee
              </Label>
              <div className="justify-end">
                <SwitchButton
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue("askWillingness", !values.askWillingness);
                  }}
                  onBlur={handleBlur}
                  value={values.askWillingness}
                  label=""
                />
              </div>
            </HStack>
            <p className="font-medium text-gray-400 text-xxs">
              Enabling this lets employees choose how much to exercise. If
              disabled, they must exercise all or none
            </p>
          </div>
          {values.askWillingness ? (
            <div className="flex-1">
              <p
                className={` form-label text-sm font-medium w-2/3 inline-flex mb-0 gap-1`}
              >
                Minimum Willingness (%)
                <Tooltip
                  text={
                    " This Sets the minimum percentage of Eligible options an employee must be willing to exercise to participate."
                  }
                  _className="w-96 left-4"
                >
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    height={20}
                    width={20}
                    className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark"
                  />
                </Tooltip>
              </p>
              <Input
                type="number"
                onBlur={handleBlur}
                disabled={inputDisabled}
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                max={100}
                name="minWillingnessPercentage"
                placeholder="Enter Minimum Willingness"
                onChange={(e) => {
                  setFieldValue("minWillingnessPercentage", e.target.value);
                  setFieldValue(
                    "monetaryDetails.optionWillingnessMultiplier",
                    0
                  );
                }}
                value={
                  values.minWillingnessPercentage === 0
                    ? ""
                    : values.minWillingnessPercentage
                }
              />
              {touched.minWillingnessPercentage &&
                errors.minWillingnessPercentage && (
                  <Error text={errors.minWillingnessPercentage}></Error>
                )}
            </div>
          ) : (
            <div className="flex-1"></div>
          )}
        </HStack>
        {values.askWillingness && (
          <HStack className="gap-8 px-6">
            <div className="flex-1">
              <Label className="text-sm font-normal">Selection of Grants</Label>
              <Select
                options={["FIFO", "Grant Price", "Custom"]}
                disabled={inputDisabled}
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                onBlur={handleBlur}
                value={values.monetaryDetails?.selectionOfGrants || "FIFO"}
                onChange={(e) => {
                  if (inputDisabled) return;
                  setFieldValue(
                    "monetaryDetails.selectionOfGrants",
                    e.target.value
                  );
                }}
              />
            </div>
            <div className="flex-1">
              <p
                className={` form-label text-sm font-medium w-2/3 inline-flex mb-0 gap-1`}
              >
                Multiples of Willingness
                <Tooltip
                  text={
                    "Specify the allowed multiples (e.g., 10, 25) in which employees can exercise their options."
                  }
                  _className="w-96 left-4"
                >
                  <Icon
                    icon="material-symbols:info-outline-rounded"
                    height={20}
                    width={20}
                    className="w-full text-xs font-medium rounded cursor-pointer text-slate-dark"
                  />
                </Tooltip>
              </p>
              <Input
                type="number"
                disabled={inputDisabled}
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                name="optionWillingnessMultiplier"
                onBlur={handleBlur}
                placeholder="Multiples of"
                onChange={(e) => {
                  if (inputDisabled) return;
                  const numericValue = e.target.value
                    ? parseFloat(e.target.value)
                    : "";
                  setFieldValue(
                    "monetaryDetails.optionWillingnessMultiplier",
                    numericValue
                  );
                  setFieldValue("monetaryDetails.minWillingnessPercentage", 0);
                }}
                value={
                  values.monetaryDetails.optionWillingnessMultiplier === 0
                    ? ""
                    : values.monetaryDetails.optionWillingnessMultiplier
                }
              />
            </div>
          </HStack>
        )}
        <HStack className="gap-8 px-6 py-4">
          <div className="flex-1 pt-4">
            <HStack className="justify-between gap-2 ">
              <Label className="text-sm font-normal">
                Consider Blackout Days for this Transaction
              </Label>
              <div className="justify-end">
                <SwitchButton
                  disabled={inputDisabled}
                  className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                  onClick={() => {
                    if (inputDisabled) return;
                    setFieldValue(
                      "monetaryDetails.considerBlackOutDays",
                      !values.monetaryDetails.considerBlackOutDays
                    );
                  }}
                  onBlur={handleBlur}
                  value={values.monetaryDetails.considerBlackOutDays || false}
                  label=""
                />
              </div>
            </HStack>
            <p className="font-medium text-gray-400 text-xxs">
              Enable this to prevent employees from exercising on blackout dates
              as defined in the company settings.
            </p>
          </div>
          <HStack className="flex-1 py-6"></HStack>
        </HStack>
        <div className="flex-1 px-6">
          <VStack className="flex-1">
            <VStack className="justify-between ">
              <Label className="text-sm font-normal">Notes</Label>
              <TextArea
                className={`${inputDisabled ? "cursor-not-allowed" : ""}`}
                disabled={inputDisabled}
                name="description"
                onBlur={handleBlur}
                placeholder="Notes"
                value={values.description}
                onChange={handleChange}
              />
            </VStack>
          </VStack>
        </div>
      </VStack>
      <HStack className="justify-between px-6 pt-8">
        <HStack>
          <ButtonPrimary1
            className=""
            onClick={() => {
              setDeleteDialogParams({ open: true });
            }}
          >
            Discard
          </ButtonPrimary1>
        </HStack>
        <HStack className="justify-end">
          <ButtonPrimary
            onClick={() => {
              handleSubmit();
            }}
          >
            Next
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default BasicExerciseDetails;
